import React, { useState, useMemo, useRef, useEffect } from 'react';

// ═══════════════════════════════════════════════════════════
//  MANUAL DE USUARIO — OFTALMETRYC
//  Sistema Integral de Gestión para Ópticas
// ═══════════════════════════════════════════════════════════

const ManualUsuario = () => {
  const [seccionActiva, setSeccionActiva] = useState('inicio');
  const [busqueda, setBusqueda] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const contentRef = useRef(null);

  // Scroll al top cuando cambia la sección
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [seccionActiva]);

  const secciones = [
    { id: 'inicio', icon: 'bi-house-fill', titulo: 'Bienvenida', grupo: 'GENERAL' },
    { id: 'primeros-pasos', icon: 'bi-rocket-takeoff', titulo: 'Primeros Pasos', grupo: 'GENERAL' },
    { id: 'login', icon: 'bi-box-arrow-in-right', titulo: 'Inicio de Sesión', grupo: 'GENERAL' },
    { id: 'dashboard', icon: 'bi-speedometer2', titulo: 'Dashboard', grupo: 'GENERAL' },
    { id: 'clientes', icon: 'bi-people-fill', titulo: 'Pacientes / Clientes', grupo: 'PACIENTES' },
    { id: 'historia-clinica', icon: 'bi-clipboard2-pulse-fill', titulo: 'Historia Clínica', grupo: 'PACIENTES' },
    { id: 'ordenes-trabajo', icon: 'bi-eyeglasses', titulo: 'Órdenes de Trabajo', grupo: 'PACIENTES' },
    { id: 'ventas', icon: 'bi-cart-check-fill', titulo: 'Punto de Venta', grupo: 'PUNTO DE VENTA' },
    { id: 'caja', icon: 'bi-safe', titulo: 'Caja Registradora', grupo: 'PUNTO DE VENTA' },
    { id: 'cuentas-cobrar', icon: 'bi-wallet2', titulo: 'Cuentas por Cobrar', grupo: 'PUNTO DE VENTA' },
    { id: 'gastos', icon: 'bi-receipt', titulo: 'Gastos / Egresos', grupo: 'PUNTO DE VENTA' },
    { id: 'facturacion', icon: 'bi-file-earmark-check-fill', titulo: 'Facturación SRI', grupo: 'PUNTO DE VENTA' },
    { id: 'productos', icon: 'bi-box-seam-fill', titulo: 'Productos', grupo: 'INVENTARIO' },
    { id: 'compras', icon: 'bi-cart-plus-fill', titulo: 'Compras', grupo: 'INVENTARIO' },
    { id: 'inventario', icon: 'bi-clipboard-data-fill', titulo: 'Stock / Kardex', grupo: 'INVENTARIO' },
    { id: 'transferencias', icon: 'bi-arrow-left-right', titulo: 'Transferencias', grupo: 'INVENTARIO' },
    { id: 'reportes', icon: 'bi-file-earmark-bar-graph', titulo: 'Centro de Reportes', grupo: 'REPORTES' },
    { id: 'conciliacion', icon: 'bi-bank2', titulo: 'Conciliación Bancaria', grupo: 'ADMINISTRACIÓN' },
    { id: 'catalogos', icon: 'bi-tags-fill', titulo: 'Catálogos', grupo: 'ADMINISTRACIÓN' },
    { id: 'sucursales', icon: 'bi-shop-window', titulo: 'Sucursales', grupo: 'ADMINISTRACIÓN' },
    { id: 'usuarios', icon: 'bi-people-fill', titulo: 'Usuarios', grupo: 'ADMINISTRACIÓN' },
    { id: 'configuracion', icon: 'bi-gear-fill', titulo: 'Configuración', grupo: 'ADMINISTRACIÓN' },
    { id: 'api', icon: 'bi-code-slash', titulo: 'API Pública', grupo: 'ADMINISTRACIÓN' },
    { id: 'faq', icon: 'bi-question-circle', titulo: 'Preguntas Frecuentes', grupo: 'AYUDA' },
    { id: 'atajos', icon: 'bi-lightning', titulo: 'Consejos y Atajos', grupo: 'AYUDA' },
  ];

  const grupos = ['GENERAL', 'PACIENTES', 'PUNTO DE VENTA', 'INVENTARIO', 'REPORTES', 'ADMINISTRACIÓN', 'AYUDA'];
  const grupoColors = {
    'GENERAL': '#6c757d', 'PACIENTES': '#0d6efd', 'PUNTO DE VENTA': '#198754',
    'INVENTARIO': '#fd7e14', 'REPORTES': '#6f42c1', 'ADMINISTRACIÓN': '#dc3545', 'AYUDA': '#0dcaf0'
  };

  const seccionesFiltradas = useMemo(() => {
    if (!busqueda.trim()) return secciones;
    const q = busqueda.toLowerCase();
    return secciones.filter(s =>
      s.titulo.toLowerCase().includes(q) || s.grupo.toLowerCase().includes(q)
    );
  }, [busqueda]);

  const navTo = (id) => {
    setSeccionActiva(id);
    if (window.innerWidth < 992) setSidebarOpen(false);
  };

  // ═══════════════════════════════════════
  //  CONTENIDO DE CADA SECCIÓN
  // ═══════════════════════════════════════
  const renderContenido = () => {
    switch (seccionActiva) {

      // ─────────────────────────────────
      //  INICIO / BIENVENIDA
      // ─────────────────────────────────
      case 'inicio':
        return (
          <div>
            {/* Hero */}
            <div className="text-center py-5 mb-4 rounded-4" style={{
              background: 'linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)',
              color: 'white'
            }}>
              <i className="bi bi-eye" style={{ fontSize: '3.5rem', opacity: 0.9 }}></i>
              <h1 className="fw-bold mt-3 mb-2">Manual de Usuario</h1>
              <h4 className="fw-light mb-3" style={{ opacity: 0.9 }}>Sistema Oftalmetryc</h4>
              <p style={{ opacity: 0.8, maxWidth: '600px', margin: '0 auto' }}>
                Guía completa para aprovechar al máximo todas las funcionalidades del sistema de gestión para su óptica.
              </p>
              <button className="btn btn-light btn-lg mt-3 fw-semibold" onClick={() => navTo('primeros-pasos')}>
                <i className="bi bi-rocket-takeoff me-2"></i>Comenzar
              </button>
            </div>

            {/* Quick Access */}
            <h4 className="fw-bold mb-3"><i className="bi bi-grid-3x3-gap me-2 text-primary"></i>Acceso Rápido</h4>
            <div className="row g-3 mb-5">
              {[
                { id: 'ventas', icon: 'bi-cart-check-fill', title: 'Punto de Venta', desc: 'Cómo realizar ventas paso a paso', color: '#198754' },
                { id: 'caja', icon: 'bi-safe', title: 'Caja Registradora', desc: 'Apertura, cierre y movimientos', color: '#0d6efd' },
                { id: 'facturacion', icon: 'bi-file-earmark-check-fill', title: 'Facturación SRI', desc: 'Facturas electrónicas y NC', color: '#fd7e14' },
                { id: 'productos', icon: 'bi-box-seam-fill', title: 'Productos', desc: 'Catálogo y especificaciones', color: '#6f42c1' },
                { id: 'clientes', icon: 'bi-people-fill', title: 'Pacientes', desc: 'Registro y gestión de clientes', color: '#dc3545' },
                { id: 'conciliacion', icon: 'bi-bank2', title: 'Conciliación', desc: 'Conciliación bancaria mensual', color: '#0dcaf0' },
              ].map((item, i) => (
                <div key={i} className="col-md-6 col-lg-4">
                  <div className="card border-0 shadow-sm h-100" style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    onClick={() => navTo(item.id)}
                    onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.12)'; }}
                    onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = ''; }}>
                    <div className="card-body d-flex align-items-start">
                      <div className="rounded-3 p-2 me-3 d-flex align-items-center justify-content-center" style={{ background: item.color + '15', minWidth: '48px', height: '48px' }}>
                        <i className={`bi ${item.icon}`} style={{ fontSize: '1.5rem', color: item.color }}></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1">{item.title}</h6>
                        <small className="text-muted">{item.desc}</small>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Módulos overview */}
            <h4 className="fw-bold mb-3"><i className="bi bi-diagram-3 me-2 text-primary"></i>Todos los Módulos</h4>
            <div className="row g-3">
              {secciones.filter(s => !['inicio', 'primeros-pasos', 'faq', 'atajos'].includes(s.id)).map((s, i) => (
                <div key={i} className="col-6 col-md-4 col-lg-3">
                  <button className="btn w-100 h-100 p-3 border text-start d-flex align-items-center"
                    style={{ borderRadius: '12px', transition: 'all 0.15s' }}
                    onClick={() => navTo(s.id)}
                    onMouseOver={e => e.currentTarget.style.borderColor = '#0d6efd'}
                    onMouseOut={e => e.currentTarget.style.borderColor = '#dee2e6'}>
                    <i className={`bi ${s.icon} me-2 text-primary`}></i>
                    <small className="fw-semibold">{s.titulo}</small>
                  </button>
                </div>
              ))}
            </div>
          </div>
        );

      // ─────────────────────────────────
      //  PRIMEROS PASOS
      // ─────────────────────────────────
      case 'primeros-pasos':
        return (
          <div>
            <SectionHeader icon="bi-rocket-takeoff" title="Primeros Pasos" subtitle="Configure su sistema en pocos minutos" />

            <InfoBox type="info" title="Antes de empezar">
              Este flujo guiado le ayudará a configurar el sistema desde cero. Siga los pasos en orden para una experiencia óptima.
            </InfoBox>

            <div className="position-relative">
              {[
                {
                  num: 1, title: 'Configurar datos de la empresa',
                  desc: 'Vaya a Configuración > General e ingrese: nombre de la empresa, RUC, dirección, teléfono, logo.',
                  link: 'configuracion', linkText: 'Ver Configuración'
                },
                {
                  num: 2, title: 'Crear sucursales',
                  desc: 'Si tiene más de un local, cree cada sucursal con sus datos de dirección y códigos SRI (establecimiento + punto de emisión).',
                  link: 'sucursales', linkText: 'Ver Sucursales'
                },
                {
                  num: 3, title: 'Crear usuarios',
                  desc: 'Cree las cuentas para cada empleado asignándoles un rol (Administrador, Optómetra, Vendedor, Cajero) y su sucursal.',
                  link: 'usuarios', linkText: 'Ver Usuarios'
                },
                {
                  num: 4, title: 'Configurar catálogos',
                  desc: 'Cree las Categorías (ej: Armazones, Lentes, Accesorios), Marcas y Proveedores que utilizará.',
                  link: 'catalogos', linkText: 'Ver Catálogos'
                },
                {
                  num: 5, title: 'Ingresar productos',
                  desc: 'Registre su catálogo de productos con precios, stock inicial, IVA y especificaciones ópticas.',
                  link: 'productos', linkText: 'Ver Productos'
                },
                {
                  num: 6, title: 'Configurar facturación electrónica',
                  desc: 'Suba su certificado digital (.p12) del SRI y configure el ambiente (Pruebas o Producción).',
                  link: 'facturacion', linkText: 'Ver Facturación SRI'
                },
                {
                  num: 7, title: 'Configurar pasarelas de pago (opcional)',
                  desc: 'Si acepta tarjeta, configure Medianet (terminal físico) y/o Payphone (pagos online).',
                  link: 'configuracion', linkText: 'Ver Configuración'
                },
                {
                  num: 8, title: '¡Listo para operar!',
                  desc: 'Abra la caja, registre pacientes, cree historias clínicas, realice ventas y emita facturas.',
                  link: 'ventas', linkText: 'Ir a Ventas'
                },
              ].map((step, i) => (
                <TimelineStep key={i} step={step} isLast={i === 7} onNavigate={navTo} />
              ))}
            </div>
          </div>
        );

      // ─────────────────────────────────
      //  LOGIN
      // ─────────────────────────────────
      case 'login':
        return (
          <div>
            <SectionHeader icon="bi-box-arrow-in-right" title="Inicio de Sesión" subtitle="Acceda de forma segura al sistema" />

            <Card title="Iniciar sesión" icon="bi-door-open">
              <StepList steps={[
                { text: 'Abra su navegador web y vaya a la dirección del sistema proporcionada por el administrador.' },
                { text: 'Ingrese su <strong>usuario o correo electrónico</strong> en el primer campo.' },
                { text: 'Ingrese su <strong>contraseña</strong>. Puede hacer clic en el ícono <i class="bi bi-eye"></i> para mostrarla.' },
                { text: 'Haga clic en <strong>"Iniciar Sesión"</strong>.' },
                { text: 'Será redirigido automáticamente al <strong>Dashboard</strong>.' },
              ]} />
            </Card>

            <Card title="Cambiar su contraseña" icon="bi-key">
              <StepList steps={[
                { text: 'Haga clic en su <strong>nombre</strong> en la esquina superior derecha del sistema.' },
                { text: 'Seleccione <strong>"Cambiar Contraseña"</strong>.' },
                { text: 'Ingrese su contraseña actual.' },
                { text: 'Ingrese la nueva contraseña (mínimo 6 caracteres) y confírmela.' },
                { text: 'Haga clic en <strong>"Guardar"</strong>.' },
              ]} />
            </Card>

            <Card title="Cerrar sesión" icon="bi-box-arrow-right">
              <p className="mb-0">Haga clic en su nombre en la esquina superior derecha y seleccione <strong>"Cerrar Sesión"</strong>.</p>
            </Card>

            <InfoBox type="warning" title="Seguridad">
              Cierre siempre su sesión al terminar la jornada laboral, especialmente en equipos compartidos.
            </InfoBox>
          </div>
        );

      // ─────────────────────────────────
      //  DASHBOARD
      // ─────────────────────────────────
      case 'dashboard':
        return (
          <div>
            <SectionHeader icon="bi-speedometer2" title="Dashboard" subtitle="Panel principal con indicadores clave" />

            <p className="lead">El Dashboard muestra un resumen en tiempo real de los indicadores más importantes de su negocio al momento de ingresar.</p>

            <h5 className="fw-bold mt-4 mb-3">Indicadores disponibles</h5>
            <div className="row g-3 mb-4">
              {[
                { icon: 'bi-cash-stack', label: 'Ventas Hoy', desc: 'Total vendido en el día actual.', color: '#198754' },
                { icon: 'bi-graph-up', label: 'Ventas del Mes', desc: 'Acumulado de ventas del mes.', color: '#0d6efd' },
                { icon: 'bi-people', label: 'Clientes', desc: 'Total de pacientes registrados.', color: '#6f42c1' },
                { icon: 'bi-eyeglasses', label: 'Órdenes Pendientes', desc: 'Órdenes aún no entregadas.', color: '#fd7e14' },
                { icon: 'bi-box-seam', label: 'Productos', desc: 'Total del catálogo.', color: '#0dcaf0' },
                { icon: 'bi-wallet2', label: 'Cuentas por Cobrar', desc: 'Saldo pendiente de créditos.', color: '#dc3545' },
                { icon: 'bi-receipt', label: 'Gastos del Mes', desc: 'Total de egresos del mes.', color: '#ffc107' },
              ].map((kpi, i) => (
                <div key={i} className="col-md-6 col-lg-4">
                  <div className="d-flex align-items-center p-3 rounded-3 border">
                    <div className="rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
                      style={{ background: kpi.color + '15', width: '44px', height: '44px' }}>
                      <i className={`bi ${kpi.icon}`} style={{ color: kpi.color, fontSize: '1.2rem' }}></i>
                    </div>
                    <div>
                      <div className="fw-bold" style={{ fontSize: '0.9rem' }}>{kpi.label}</div>
                      <small className="text-muted">{kpi.desc}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Card title="Secciones adicionales" icon="bi-bar-chart">
              <ul className="mb-0">
                <li><strong>Top 10 Productos:</strong> Los productos más vendidos del período.</li>
                <li><strong>Ventas por Categoría:</strong> Distribución de ventas por tipo de producto.</li>
                <li><strong>Métodos de Pago:</strong> Desglose por efectivo, tarjeta, transferencia y crédito.</li>
                <li><strong>Últimos Movimientos:</strong> Transacciones recientes en el sistema.</li>
                <li><strong>Últimas Ventas:</strong> Ventas más recientes con cliente, monto y estado.</li>
              </ul>
            </Card>

            <InfoBox type="info" title="Sucursal">
              Si tiene varias sucursales, los datos corresponden a la sucursal seleccionada en el encabezado. Cambie de sucursal para ver los KPIs de otro local.
            </InfoBox>
          </div>
        );

      // ─────────────────────────────────
      //  CLIENTES / PACIENTES
      // ─────────────────────────────────
      case 'clientes':
        return (
          <div>
            <SectionHeader icon="bi-people-fill" title="Pacientes / Clientes" subtitle="Gestión integral de su base de pacientes" />

            <Card title="Registrar nuevo paciente" icon="bi-person-plus">
              <StepList steps={[
                { text: 'Haga clic en el botón <strong>"+ Nuevo Cliente"</strong>.' },
                { text: 'Seleccione <strong>Tipo de persona</strong>: Natural (persona) o Jurídica (empresa).' },
                { text: 'Seleccione <strong>Tipo de identificación</strong>: Cédula, RUC o Pasaporte.' },
                { text: 'Ingrese el <strong>número de identificación</strong>. El sistema verifica automáticamente que no esté duplicado.' },
                { text: 'Complete los <strong>datos personales</strong>: Nombres, Apellidos (o Razón Social si es jurídica).' },
                { text: 'Complete los <strong>datos de contacto</strong>: Teléfono, Celular, Email, WhatsApp, Dirección, Ciudad, Provincia.' },
                { text: 'Complete la <strong>información médica</strong> (opcional): Ocupación, Actividad, Hobby, Horas computador/celular, Usa lentes.' },
                { text: 'Seleccione <strong>tipo de cliente</strong>: Regular o Convenio empresa. Si es convenio, asigne un límite de crédito.' },
                { text: 'Haga clic en <strong>"Guardar"</strong>.' },
              ]} />
            </Card>

            <Card title="Buscar pacientes" icon="bi-search">
              <p>Use el <strong>campo de búsqueda</strong> en la parte superior del listado. Puede buscar por:</p>
              <div className="d-flex flex-wrap gap-2 mb-2">
                <span className="badge bg-light text-dark border">Nombre</span>
                <span className="badge bg-light text-dark border">Apellido</span>
                <span className="badge bg-light text-dark border">Cédula / RUC</span>
                <span className="badge bg-light text-dark border">Email</span>
                <span className="badge bg-light text-dark border">Teléfono</span>
              </div>
            </Card>

            <Card title="Editar paciente" icon="bi-pencil-square">
              <p className="mb-0">Haga clic en el ícono de <strong>editar</strong> <i className="bi bi-pencil"></i> del paciente. Modifique los campos necesarios y guarde.</p>
            </Card>

            <FieldTable fields={[
              { field: 'Tipo persona', desc: 'Natural (persona física) o Jurídica (empresa).' },
              { field: 'Tipo identificación', desc: 'Cédula (10 dígitos), RUC (13 dígitos) o Pasaporte.' },
              { field: 'Tipo cliente', desc: 'Regular o Convenio empresa (con condiciones especiales y crédito).' },
              { field: 'Límite crédito', desc: 'Monto máximo que puede adeudar el cliente en ventas a crédito.' },
            ]} />
          </div>
        );

      // ─────────────────────────────────
      //  HISTORIA CLÍNICA
      // ─────────────────────────────────
      case 'historia-clinica':
        return (
          <div>
            <SectionHeader icon="bi-clipboard2-pulse-fill" title="Historia Clínica" subtitle="Registro oftalmológico completo" />

            <Card title="Crear nueva historia clínica" icon="bi-plus-circle">
              <StepList steps={[
                { text: 'Vaya a <strong>Historia Clínica</strong> en el menú lateral.' },
                { text: 'Se abre el formulario. Busque y seleccione el <strong>paciente</strong> (por nombre o cédula).' },
                { text: 'Complete las secciones del examen según corresponda (ver tabla abajo).' },
                { text: 'No es obligatorio completar todas las secciones, solo las que apliquen al examen.' },
                { text: 'Haga clic en <strong>"Guardar Historia Clínica"</strong>.' },
              ]} />
            </Card>

            <h5 className="fw-bold mt-4 mb-3">Secciones del examen oftalmológico</h5>
            <div className="row g-3 mb-4">
              {[
                { icon: 'bi-eye', title: 'Agudeza Visual', desc: 'AV lejos/cerca, OD/OI, sin y con corrección.' },
                { icon: 'bi-rulers', title: 'Lensometría', desc: 'Medición lentes actuales: Esf, Cyl, Eje, Add, DP, Alt.' },
                { icon: 'bi-circle', title: 'Queratometría', desc: 'Curvatura corneal: K1, K2 y eje.' },
                { icon: 'bi-cpu', title: 'Autorefractor', desc: 'Lectura automática: Esfera, Cilindro, Eje.' },
                { icon: 'bi-crosshair', title: 'Subjetivo', desc: 'Refracción subjetiva completa OD/OI.' },
                { icon: 'bi-prescription2', title: 'RX Final', desc: 'Prescripción final para el paciente.' },
                { icon: 'bi-disc', title: 'Lentes de Contacto', desc: 'Curva base, diámetro, poder, marca, tipo.' },
                { icon: 'bi-arrows-move', title: 'Cover Test', desc: 'Forias, tropías y magnitud.' },
                { icon: 'bi-arrows-fullscreen', title: 'Motilidad Ocular', desc: 'Evaluación de movimientos oculares.' },
                { icon: 'bi-arrow-down-right-circle', title: 'PPC', desc: 'Punto Próximo de Convergencia.' },
                { icon: 'bi-speedometer', title: 'PIO', desc: 'Presión Intraocular OD/OI.' },
                { icon: 'bi-search', title: 'Biomicroscopía', desc: 'Lámpara de hendidura: córnea, cristalino, pupila, iris.' },
                { icon: 'bi-bullseye', title: 'Fondo de Ojo', desc: 'Retina, nervio óptico, mácula, vasos.' },
                { icon: 'bi-heart-pulse', title: 'Signos Vitales', desc: 'PA, saturación O2, glucosa, triglicéridos.' },
                { icon: 'bi-clipboard-check', title: 'Diagnóstico', desc: 'Diagnóstico, tratamiento, disposición, conducta.' },
              ].map((sec, i) => (
                <div key={i} className="col-md-6 col-lg-4">
                  <div className="d-flex align-items-start p-2 rounded border bg-white">
                    <i className={`bi ${sec.icon} text-primary me-2 mt-1`}></i>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{sec.title}</div>
                      <small className="text-muted" style={{ fontSize: '0.78rem' }}>{sec.desc}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <InfoBox type="info" title="Nomenclatura">
              <strong>OD</strong> = Ojo Derecho &nbsp;|&nbsp; <strong>OI</strong> = Ojo Izquierdo &nbsp;|&nbsp;
              <strong>SC</strong> = Sin Corrección &nbsp;|&nbsp; <strong>CC</strong> = Con Corrección &nbsp;|&nbsp;
              <strong>DP</strong> = Distancia Pupilar &nbsp;|&nbsp; <strong>Add</strong> = Adición
            </InfoBox>
          </div>
        );

      // ─────────────────────────────────
      //  ÓRDENES DE TRABAJO
      // ─────────────────────────────────
      case 'ordenes-trabajo':
        return (
          <div>
            <SectionHeader icon="bi-eyeglasses" title="Órdenes de Trabajo" subtitle="Control de pedidos al laboratorio óptico" />

            <Card title="Crear orden de trabajo" icon="bi-plus-circle">
              <StepList steps={[
                { text: 'Haga clic en <strong>"+ Nueva Orden"</strong>.' },
                { text: 'Seleccione el <strong>cliente</strong> con el buscador.' },
                { text: 'Seleccione el <strong>Tipo</strong>: Armazón completo, Solo lentes, Reparación, Adaptación L.C. u Otro.' },
                { text: 'Seleccione la <strong>Prioridad</strong>: Baja, Normal, Alta o Urgente.' },
                { text: 'Escriba la <strong>descripción</strong> detallada del trabajo.' },
                { text: 'Establezca la <strong>fecha estimada de entrega</strong>.' },
                { text: 'Haga clic en <strong>"Guardar"</strong>.' },
              ]} />
            </Card>

            <h5 className="fw-bold mt-4 mb-3">Flujo de estados</h5>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-4 p-3 bg-light rounded-3">
              <StatusBadge color="secondary" label="Recibida" />
              <i className="bi bi-arrow-right text-muted"></i>
              <StatusBadge color="primary" label="En Laboratorio" />
              <i className="bi bi-arrow-right text-muted"></i>
              <StatusBadge color="info" label="Listo" />
              <i className="bi bi-arrow-right text-muted"></i>
              <StatusBadge color="success" label="Entregada" />
              <div className="ms-3 text-muted">|</div>
              <StatusBadge color="danger" label="Cancelada" />
            </div>

            <Card title="Vistas disponibles" icon="bi-layout-three-columns">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="p-3 border rounded-3 h-100">
                    <h6 className="fw-bold"><i className="bi bi-table me-2"></i>Vista Tabla</h6>
                    <small className="text-muted">Lista tradicional con filtros por estado, tipo, prioridad. Incluye paginación y búsqueda.</small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 border rounded-3 h-100">
                    <h6 className="fw-bold"><i className="bi bi-kanban me-2"></i>Vista Kanban</h6>
                    <small className="text-muted">Tablero visual con columnas por estado. Ideal para ver el flujo de trabajo de un vistazo.</small>
                  </div>
                </div>
              </div>
            </Card>

            <Card title="Cambiar estado" icon="bi-arrow-repeat">
              <p className="mb-0">Haga clic en el botón de <strong>cambio de estado</strong> de la orden. Opcionalmente agregue notas y confirme el cambio.</p>
            </Card>
          </div>
        );

      // ─────────────────────────────────
      //  PUNTO DE VENTA
      // ─────────────────────────────────
      case 'ventas':
        return (
          <div>
            <SectionHeader icon="bi-cart-check-fill" title="Punto de Venta" subtitle="Ventas con múltiples métodos de pago" />

            <InfoBox type="warning" title="Requisito previo">
              Debe tener una <strong>caja abierta</strong> para realizar ventas. Si la caja está cerrada, vaya primero a <button className="btn btn-link p-0 fw-bold" onClick={() => navTo('caja')}>Caja Registradora</button>.
            </InfoBox>

            <Card title="Venta al contado" icon="bi-cash-coin">
              <StepList steps={[
                { text: 'Busque y seleccione el <strong>cliente</strong>. Si no existe, use <strong>"+ Nuevo"</strong> para crearlo rápidamente.' },
                { text: 'Agregue productos: busque por código o nombre y haga clic en <strong>"Agregar"</strong>.' },
                { text: 'Ajuste <strong>cantidades</strong> si es necesario.' },
                { text: 'Aplique <strong>descuento</strong> si corresponde (porcentaje).' },
                { text: 'Seleccione el <strong>método de pago</strong> (ver tabla abajo).' },
                { text: 'Complete los datos del pago y haga clic en <strong>"Completar Venta"</strong>.' },
              ]} />
            </Card>

            <h5 className="fw-bold mt-4 mb-3">Métodos de pago disponibles</h5>
            <div className="row g-3 mb-4">
              {[
                { icon: 'bi-cash', title: 'Efectivo', desc: 'Ingrese el monto recibido. El sistema calcula el cambio automáticamente.', color: '#198754' },
                { icon: 'bi-credit-card', title: 'Tarjeta Medianet', desc: 'Terminal físico. Requiere código de autorización y últimos 4 dígitos de la tarjeta.', color: '#0d6efd' },
                { icon: 'bi-phone', title: 'Tarjeta Payphone', desc: 'Pasarela online. Se abre la página de pago de Payphone para el cliente.', color: '#6f42c1' },
                { icon: 'bi-bank', title: 'Transferencia', desc: 'Transferencia bancaria. Ingrese el número de referencia/comprobante.', color: '#fd7e14' },
                { icon: 'bi-shuffle', title: 'Mixto', desc: 'Combine dos o más métodos. Ej: parte efectivo + parte tarjeta.', color: '#0dcaf0' },
              ].map((m, i) => (
                <div key={i} className="col-md-6">
                  <div className="d-flex align-items-start p-3 border rounded-3">
                    <div className="rounded-3 p-2 me-3 d-flex align-items-center justify-content-center" style={{ background: m.color + '15', minWidth: '40px', height: '40px' }}>
                      <i className={`bi ${m.icon}`} style={{ color: m.color }}></i>
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1">{m.title}</h6>
                      <small className="text-muted">{m.desc}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Card title="Venta a crédito" icon="bi-calendar-check">
              <StepList steps={[
                { text: 'Agregue cliente y productos al carrito como en una venta normal.' },
                { text: 'Seleccione método de pago <strong>"Crédito"</strong>.' },
                { text: 'El sistema muestra la fecha de vencimiento automáticamente.' },
                { text: 'Ingrese un <strong>abono inicial obligatorio</strong>: seleccione método y monto del abono.' },
                { text: 'Complete la venta. El saldo pendiente aparecerá en <strong>Cuentas por Cobrar</strong>.' },
              ]} />
            </Card>

            <Card title="Historial y tickets" icon="bi-clock-history">
              <p className="mb-0">En la sección <strong>Historial</strong> del módulo de ventas puede ver todas las ventas, filtrar por fecha/estado, y <strong>reimprimir tickets</strong>.</p>
            </Card>

            <InfoBox type="info" title="Auto-facturación">
              Si la auto-facturación está activada en Configuración, cada venta genera automáticamente una factura electrónica al SRI.
            </InfoBox>
          </div>
        );

      // ─────────────────────────────────
      //  CAJA REGISTRADORA
      // ─────────────────────────────────
      case 'caja':
        return (
          <div>
            <SectionHeader icon="bi-safe" title="Caja Registradora" subtitle="Apertura, cierre y control de efectivo" />

            <InfoBox type="warning" title="Permisos">
              Solo usuarios con rol de <strong>Administrador</strong> o <strong>Gerente</strong> pueden abrir y cerrar cajas.
            </InfoBox>

            <Card title="Abrir caja" icon="bi-unlock">
              <StepList steps={[
                { text: 'Si no hay caja abierta, verá el formulario de apertura automáticamente.' },
                { text: 'Ingrese el <strong>monto de apertura</strong> (efectivo inicial en caja).' },
                { text: 'Opcionalmente cuente las <strong>denominaciones</strong>: $100, $50, $20, $10, $5, $1, $0.50, $0.25, $0.10, $0.05, $0.01.' },
                { text: 'Ingrese sus <strong>credenciales de administrador</strong> para autorizar.' },
                { text: 'Seleccione el <strong>cajero</strong> que operará.' },
                { text: 'Haga clic en <strong>"Abrir Caja"</strong>.' },
              ]} />
            </Card>

            <Card title="Registrar movimientos" icon="bi-arrow-down-up">
              <p>Con la caja abierta, registre movimientos adicionales:</p>
              <div className="d-flex flex-wrap gap-3 mb-3">
                <div className="flex-fill p-3 border rounded-3" style={{ background: '#19875415', minWidth: '200px' }}>
                  <h6 className="fw-bold text-success mb-1"><i className="bi bi-arrow-down-circle me-1"></i>Ingreso</h6>
                  <small>Dinero que entra (no por venta). Ej: cambio de billetes.</small>
                </div>
                <div className="flex-fill p-3 border rounded-3" style={{ background: '#dc354515', minWidth: '200px' }}>
                  <h6 className="fw-bold text-danger mb-1"><i className="bi bi-arrow-up-circle me-1"></i>Egreso</h6>
                  <small>Dinero que sale. Ej: pago a proveedor.</small>
                </div>
                <div className="flex-fill p-3 border rounded-3" style={{ background: '#ffc10715', minWidth: '200px' }}>
                  <h6 className="fw-bold text-warning mb-1"><i className="bi bi-dash-circle me-1"></i>Retiro</h6>
                  <small>Retiro parcial. Ej: envío al banco.</small>
                </div>
              </div>
              <StepList steps={[
                { text: 'Haga clic en <strong>"+ Movimiento"</strong>, seleccione tipo, monto y descripción.' },
                { text: 'Haga clic en <strong>"Guardar"</strong>.' },
              ]} />
            </Card>

            <Card title="Cerrar caja" icon="bi-lock">
              <StepList steps={[
                { text: 'Haga clic en <strong>"Cerrar Caja"</strong>.' },
                { text: 'Cuente el dinero físico e ingrese el <strong>monto real</strong>.' },
                { text: 'Opcionalmente ingrese el conteo de denominaciones.' },
                { text: 'El sistema compara <strong>monto esperado vs. real</strong>, mostrando sobrante o faltante.' },
                { text: 'Confirme el cierre.' },
              ]} />
            </Card>

            <Card title="Historial" icon="bi-clock-history">
              <p className="mb-0">La pestaña <strong>"Historial"</strong> muestra todas las cajas anteriores: apertura, cierre, diferencias y detalle de movimientos.</p>
            </Card>
          </div>
        );

      // ─────────────────────────────────
      //  CUENTAS POR COBRAR
      // ─────────────────────────────────
      case 'cuentas-cobrar':
        return (
          <div>
            <SectionHeader icon="bi-wallet2" title="Cuentas por Cobrar" subtitle="Control de ventas a crédito y abonos" />

            <Card title="Ver cuentas pendientes" icon="bi-list-check">
              <p>Al ingresar verá un resumen con:</p>
              <div className="d-flex flex-wrap gap-3 mb-3">
                <div className="p-3 border rounded-3 text-center" style={{ minWidth: '150px' }}>
                  <i className="bi bi-currency-dollar text-primary" style={{ fontSize: '1.5rem' }}></i>
                  <div className="fw-bold mt-1">Total por Cobrar</div>
                  <small className="text-muted">Suma de todos los saldos</small>
                </div>
                <div className="p-3 border rounded-3 text-center" style={{ minWidth: '150px' }}>
                  <i className="bi bi-hash text-success" style={{ fontSize: '1.5rem' }}></i>
                  <div className="fw-bold mt-1">Cantidad</div>
                  <small className="text-muted">Total de cuentas</small>
                </div>
                <div className="p-3 border rounded-3 text-center" style={{ minWidth: '150px' }}>
                  <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: '1.5rem' }}></i>
                  <div className="fw-bold mt-1">Monto Vencido</div>
                  <small className="text-muted">Cuentas pasadas de fecha</small>
                </div>
              </div>
              <p className="mb-0">Use los filtros para buscar por <strong>cliente, estado o rango de fechas</strong>.</p>
            </Card>

            <Card title="Registrar un abono" icon="bi-plus-circle">
              <StepList steps={[
                { text: 'Haga clic en <strong>"Ver Detalle"</strong> de la cuenta.' },
                { text: 'Verá: detalle de la venta, historial de abonos y saldo pendiente.' },
                { text: 'Haga clic en <strong>"+ Registrar Abono"</strong>.' },
                { text: 'Ingrese el <strong>monto</strong> del abono.' },
                { text: 'Seleccione el <strong>método de pago</strong> (Efectivo, Tarjeta o Transferencia).' },
                { text: 'Si es tarjeta: código de autorización y últimos 4 dígitos. Si es transferencia: número de referencia.' },
                { text: 'Haga clic en <strong>"Registrar Abono"</strong>.' },
              ]} />
            </Card>

            <h5 className="fw-bold mt-4 mb-3">Estados</h5>
            <div className="d-flex flex-wrap gap-3 mb-3">
              <StatusBadge color="warning" label="Pendiente" desc="Saldo total sin pagar" />
              <StatusBadge color="info" label="Parcial" desc="Abonos registrados, queda saldo" />
              <StatusBadge color="success" label="Pagada" desc="Completamente saldada" />
            </div>

            <InfoBox type="info" title="Pago automático">
              Cuando el saldo llega a <strong>$0.00</strong>, la cuenta se marca automáticamente como <strong>Pagada</strong>.
            </InfoBox>
          </div>
        );

      // ─────────────────────────────────
      //  GASTOS / EGRESOS
      // ─────────────────────────────────
      case 'gastos':
        return (
          <div>
            <SectionHeader icon="bi-receipt" title="Gastos / Egresos" subtitle="Control de gastos operativos y caja chica" />

            <Card title="Registrar un gasto" icon="bi-plus-circle">
              <StepList steps={[
                { text: 'Haga clic en <strong>"+ Nuevo Gasto"</strong>.' },
                { text: 'Seleccione la <strong>categoría</strong> (ej: Servicios básicos, Arriendos, Suministros).' },
                { text: 'Ingrese el <strong>concepto</strong> o descripción del gasto.' },
                { text: 'Ingrese el <strong>monto</strong>.' },
                { text: 'Seleccione el <strong>método de pago</strong>: Efectivo, Transferencia o Cheque.' },
                { text: 'Si es transferencia/cheque, ingrese el <strong>número de documento</strong>.' },
                { text: 'Seleccione el <strong>proveedor</strong> (opcional) y la <strong>fecha</strong>.' },
                { text: 'Haga clic en <strong>"Guardar"</strong>.' },
              ]} />
            </Card>

            <Card title="Categorías de gastos" icon="bi-tags">
              <p>Personalice las categorías desde la pestaña <strong>"Categorías"</strong>:</p>
              <StepList steps={[
                { text: 'Haga clic en <strong>"+ Nueva Categoría"</strong>.' },
                { text: 'Ingrese nombre, seleccione ícono y color.' },
                { text: 'Guarde. La categoría estará disponible al registrar gastos.' },
              ]} />
            </Card>

            <Card title="Caja Chica" icon="bi-piggy-bank">
              <p>Fondo para gastos menores del día a día:</p>
              <ul>
                <li><strong>Abrir:</strong> Haga clic en "Abrir Caja Chica" e ingrese el monto inicial.</li>
                <li><strong>Usar:</strong> Los gastos con método "Efectivo" se descuentan automáticamente.</li>
                <li><strong>Reponer:</strong> Cuando el fondo se agote, use "Reponer Caja Chica".</li>
                <li><strong>Consultar:</strong> El saldo actual se muestra en la parte superior.</li>
              </ul>
            </Card>

            <Card title="Anular gasto" icon="bi-x-circle">
              <p className="mb-0">Haga clic en el botón <strong>anular</strong> <i className="bi bi-x-circle"></i>, ingrese el motivo y confirme. El gasto queda registrado como <em>anulado</em> (no se elimina).</p>
            </Card>
          </div>
        );

      // ─────────────────────────────────
      //  FACTURACIÓN SRI
      // ─────────────────────────────────
      case 'facturacion':
        return (
          <div>
            <SectionHeader icon="bi-file-earmark-check-fill" title="Facturación Electrónica SRI" subtitle="Facturas y notas de crédito al SRI Ecuador" />

            <InfoBox type="warning" title="Configuración previa">
              Debe tener configurado el <strong>certificado digital (.p12)</strong> y los datos del SRI en <button className="btn btn-link p-0 fw-bold" onClick={() => navTo('configuracion')}>Configuración</button>.
            </InfoBox>

            <Card title="Emisión de facturas" icon="bi-file-earmark-text">
              <p><strong>Dos formas de emitir:</strong></p>
              <div className="row g-3 mb-2">
                <div className="col-md-6">
                  <div className="p-3 border rounded-3 h-100" style={{ background: '#19875410' }}>
                    <h6 className="fw-bold text-success"><i className="bi bi-lightning me-1"></i>Automática</h6>
                    <small>Si la auto-facturación está activa, cada venta genera factura al SRI automáticamente.</small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 border rounded-3 h-100" style={{ background: '#0d6efd10' }}>
                    <h6 className="fw-bold text-primary"><i className="bi bi-hand-index me-1"></i>Manual</h6>
                    <small>Desde el historial de ventas o este módulo, emita la factura manualmente.</small>
                  </div>
                </div>
              </div>
            </Card>

            <h5 className="fw-bold mt-4 mb-3">Estados de comprobantes</h5>
            <div className="d-flex flex-wrap gap-2 mb-4 p-3 bg-light rounded-3">
              <StatusBadge color="secondary" label="Borrador" desc="No enviado" />
              <StatusBadge color="warning" label="Enviado" desc="Esperando SRI" />
              <StatusBadge color="success" label="Autorizado" desc="Válido" />
              <StatusBadge color="danger" label="Rechazado" desc="Error SRI" />
              <StatusBadge color="dark" label="Anulado" desc="Con NC" />
            </div>

            <Card title="Emitir Nota de Crédito" icon="bi-file-earmark-minus">
              <StepList steps={[
                { text: 'Vaya a la pestaña <strong>"Notas de Crédito"</strong>.' },
                { text: 'Localice la factura y haga clic en <strong>"Emitir NC"</strong>.' },
                { text: 'Seleccione el <strong>motivo</strong>: Devolución, Descuento, Error o Otro.' },
                { text: 'Si es devolución, seleccione los <strong>productos a devolver</strong> y cantidades.' },
                { text: 'Confirme. La NC se envía al SRI y el stock se ajusta automáticamente si hay devolución.' },
              ]} />
            </Card>

            <InfoBox type="info" title="Certificado digital">
              El estado del certificado se muestra en la parte superior. Si está vencido, renuévelo en el portal del SRI y súbalo en <strong>Configuración > SRI</strong>.
            </InfoBox>
          </div>
        );

      // ─────────────────────────────────
      //  PRODUCTOS
      // ─────────────────────────────────
      case 'productos':
        return (
          <div>
            <SectionHeader icon="bi-box-seam-fill" title="Productos" subtitle="Catálogo con especificaciones ópticas" />

            <Card title="Crear un producto" icon="bi-plus-circle">
              <h6 className="fw-bold text-primary"><i className="bi bi-1-circle me-2"></i>Datos Básicos</h6>
              <ul>
                <li><strong>Código:</strong> Único, se valida que no exista duplicado.</li>
                <li><strong>Nombre:</strong> Nombre comercial del producto.</li>
                <li><strong>Tipo:</strong> Armazón, Lente Oftálmico, Lente de Sol, Lente de Contacto, Solución, Accesorio, Equipo, Medicamento.</li>
                <li><strong>Categoría, Marca, Proveedor:</strong> De los catálogos existentes.</li>
                <li><strong>Precio compra / Precio venta / IVA.</strong></li>
                <li><strong>Stock:</strong> Actual, mínimo y máximo.</li>
                <li><strong>Imagen:</strong> Suba una foto o tome una con la cámara.</li>
              </ul>

              <h6 className="fw-bold text-primary mt-3"><i className="bi bi-2-circle me-2"></i>Especificaciones Ópticas (según tipo)</h6>
              <div className="row g-2">
                <div className="col-md-4">
                  <div className="p-2 border rounded text-center">
                    <i className="bi bi-eyeglasses text-primary d-block" style={{ fontSize: '1.3rem' }}></i>
                    <small className="fw-bold">Armazones</small><br />
                    <small className="text-muted">Puente, varilla, género, material</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-2 border rounded text-center">
                    <i className="bi bi-circle text-primary d-block" style={{ fontSize: '1.3rem' }}></i>
                    <small className="fw-bold">Lentes</small><br />
                    <small className="text-muted">Tipo, material, índice, rangos</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-2 border rounded text-center">
                    <i className="bi bi-disc text-primary d-block" style={{ fontSize: '1.3rem' }}></i>
                    <small className="fw-bold">Contacto</small><br />
                    <small className="text-muted">Tipo, uso, radio, diámetro</small>
                  </div>
                </div>
              </div>
            </Card>

            <Card title="Buscar y filtrar" icon="bi-funnel">
              <p className="mb-1">Filtros disponibles en la parte superior:</p>
              <div className="d-flex flex-wrap gap-2">
                {['Texto libre', 'Categoría', 'Marca', 'Tipo', 'Género'].map(f => (
                  <span key={f} className="badge bg-light text-dark border">{f}</span>
                ))}
              </div>
              <p className="text-muted mt-2 mb-0"><small>Paginación: 20 productos por página.</small></p>
            </Card>
          </div>
        );

      // ─────────────────────────────────
      //  COMPRAS
      // ─────────────────────────────────
      case 'compras':
        return (
          <div>
            <SectionHeader icon="bi-cart-plus-fill" title="Compras" subtitle="Órdenes de compra a proveedores" />

            <Card title="Registrar una compra" icon="bi-plus-circle">
              <StepList steps={[
                { text: 'Haga clic en <strong>"+ Nueva Compra"</strong>.' },
                { text: 'Seleccione el <strong>proveedor</strong>.' },
                { text: 'Ingrese el <strong>número de factura</strong> del proveedor.' },
                { text: 'Seleccione la <strong>fecha</strong>.' },
                { text: 'Para cada producto: búsquelo, ingrese <strong>cantidad</strong>, <strong>precio unitario</strong> y <strong>% IVA</strong>. Haga clic en "Agregar".' },
                { text: 'Verifique el <strong>total</strong> (subtotal + IVA).' },
                { text: 'Haga clic en <strong>"Guardar Compra"</strong>.' },
              ]} />
            </Card>

            <h5 className="fw-bold mt-4 mb-3">Estados</h5>
            <div className="d-flex flex-wrap gap-2 mb-3 p-3 bg-light rounded-3 align-items-center">
              <StatusBadge color="warning" label="Pendiente" desc="Mercadería no recibida" />
              <i className="bi bi-arrow-right text-muted"></i>
              <StatusBadge color="success" label="Recibida" desc="Stock actualizado" />
              <div className="ms-2 text-muted">|</div>
              <StatusBadge color="danger" label="Cancelada" />
            </div>

            <InfoBox type="success" title="Stock automático">
              Al marcar una compra como <strong>"Recibida"</strong>, el stock de cada producto se incrementa automáticamente con las cantidades compradas.
            </InfoBox>
          </div>
        );

      // ─────────────────────────────────
      //  INVENTARIO / KARDEX
      // ─────────────────────────────────
      case 'inventario':
        return (
          <div>
            <SectionHeader icon="bi-clipboard-data-fill" title="Stock / Kardex" subtitle="Control de inventario en tiempo real" />

            <Card title="Inventario general" icon="bi-box-seam">
              <p>Al ingresar verá un resumen:</p>
              <div className="d-flex flex-wrap gap-3 mb-3">
                {[
                  { icon: 'bi-boxes', label: 'Total Productos', color: '#0d6efd' },
                  { icon: 'bi-currency-dollar', label: 'Valor Inventario', color: '#198754' },
                  { icon: 'bi-exclamation-triangle', label: 'Sin Stock', color: '#dc3545' },
                ].map((s, i) => (
                  <div key={i} className="p-3 border rounded-3 text-center flex-fill" style={{ minWidth: '140px' }}>
                    <i className={`bi ${s.icon} d-block`} style={{ fontSize: '1.5rem', color: s.color }}></i>
                    <div className="fw-bold mt-1" style={{ fontSize: '0.85rem' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </Card>

            <h5 className="fw-bold mt-4 mb-3">Indicadores de stock</h5>
            <div className="d-flex flex-wrap gap-2 mb-4">
              <StatusBadge color="danger" label="Sin Stock" desc="Stock = 0" />
              <StatusBadge color="warning" label="Stock Bajo" desc="≤ Mínimo" />
              <StatusBadge color="success" label="Normal" desc="En rango" />
              <StatusBadge color="info" label="Stock Alto" desc="≥ Máximo" />
            </div>

            <Card title="Ajuste de stock" icon="bi-wrench">
              <StepList steps={[
                { text: 'Localice el producto y haga clic en el botón de <strong>ajuste</strong> <i class="bi bi-wrench"></i>.' },
                { text: 'Seleccione <strong>Entrada</strong> (suma) o <strong>Salida</strong> (resta).' },
                { text: 'Ingrese <strong>cantidad</strong> y <strong>motivo</strong> (ej: "Conteo físico", "Producto dañado").' },
                { text: 'Confirme el ajuste.' },
              ]} />
            </Card>

            <Card title="Kardex (historial de movimientos)" icon="bi-clock-history">
              <p>Haga clic en el ícono de <strong>Kardex</strong> <i className="bi bi-clock-history"></i> de cualquier producto para ver:</p>
              <ul className="mb-0">
                <li>Fecha y hora de cada movimiento.</li>
                <li>Tipo: entrada, salida, ajuste, venta, compra, transferencia.</li>
                <li>Cantidad, stock anterior y stock posterior.</li>
                <li>Motivo y referencia del movimiento.</li>
              </ul>
            </Card>

            <Card title="Exportar" icon="bi-download">
              <p className="mb-0">Use los botones <strong>"Exportar Excel"</strong> o <strong>"Exportar PDF"</strong> para descargar el inventario con los filtros aplicados.</p>
            </Card>
          </div>
        );

      // ─────────────────────────────────
      //  TRANSFERENCIAS
      // ─────────────────────────────────
      case 'transferencias':
        return (
          <div>
            <SectionHeader icon="bi-arrow-left-right" title="Transferencias entre Sucursales" subtitle="Envío de productos entre locales" />

            <InfoBox type="info" title="Nota">
              Este módulo solo aplica si tiene más de una sucursal configurada.
            </InfoBox>

            <Card title="Crear transferencia" icon="bi-plus-circle">
              <StepList steps={[
                { text: 'Haga clic en <strong>"+ Nueva Transferencia"</strong>.' },
                { text: 'La sucursal <strong>origen</strong> es la actual automáticamente.' },
                { text: 'Seleccione la sucursal <strong>destino</strong>.' },
                { text: 'Agregue productos: busque por código/nombre, ingrese cantidad. El sistema muestra stock disponible.' },
                { text: 'Haga clic en <strong>"Crear Transferencia"</strong>.' },
              ]} />
            </Card>

            <h5 className="fw-bold mt-4 mb-3">Flujo de estados</h5>
            <div className="p-3 bg-light rounded-3 mb-4">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <StatusBadge color="warning" label="Pendiente" />
                <i className="bi bi-arrow-right text-muted"></i>
                <StatusBadge color="primary" label="Enviada" />
                <i className="bi bi-arrow-right text-muted"></i>
                <StatusBadge color="success" label="Completada" />
              </div>
              <div className="mt-2 d-flex gap-2">
                <StatusBadge color="danger" label="Rechazada" />
                <StatusBadge color="secondary" label="Cancelada" />
              </div>
            </div>

            <InfoBox type="success" title="Stock automático">
              Al completar una transferencia, el stock se descuenta en origen y se suma en destino automáticamente.
            </InfoBox>
          </div>
        );

      // ─────────────────────────────────
      //  REPORTES
      // ─────────────────────────────────
      case 'reportes':
        return (
          <div>
            <SectionHeader icon="bi-file-earmark-bar-graph" title="Centro de Reportes" subtitle="Exportación de datos en Excel y PDF" />

            <Card title="Cómo generar un reporte" icon="bi-play-circle">
              <StepList steps={[
                { text: 'Seleccione la pestaña del reporte deseado.' },
                { text: 'Configure los <strong>filtros</strong> (fechas, estados, categorías, etc.).' },
                { text: 'Seleccione el <strong>formato</strong>: Excel (.xlsx) o PDF.' },
                { text: 'Haga clic en <strong>"Generar Reporte"</strong>.' },
                { text: 'El archivo se descarga automáticamente.' },
              ]} />
            </Card>

            <h5 className="fw-bold mt-4 mb-3">Reportes disponibles</h5>
            <div className="row g-3">
              {[
                { icon: 'bi-cart-check', title: 'Reporte de Ventas', color: '#198754',
                  filtros: ['Rango de fechas', 'Estado (completada/anulada)', 'Método de pago'],
                  contenido: 'Cliente, productos, totales, impuestos, método de pago, estado.' },
                { icon: 'bi-wallet2', title: 'Cuentas por Cobrar', color: '#0d6efd',
                  filtros: ['Rango de fechas', 'Estado (pendiente/parcial/pagada/vencida)'],
                  contenido: 'Cliente, monto total, saldo, vencimiento, abonos.' },
                { icon: 'bi-receipt', title: 'Reporte de Gastos', color: '#dc3545',
                  filtros: ['Rango de fechas', 'Categoría', 'Estado (registrado/pagado/anulado)'],
                  contenido: 'Categoría, concepto, monto, método de pago, proveedor.' },
              ].map((r, i) => (
                <div key={i} className="col-lg-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                      <div className="text-center mb-3">
                        <div className="rounded-circle mx-auto d-flex align-items-center justify-content-center" style={{ width: '56px', height: '56px', background: r.color + '15' }}>
                          <i className={`bi ${r.icon}`} style={{ fontSize: '1.5rem', color: r.color }}></i>
                        </div>
                      </div>
                      <h6 className="fw-bold text-center mb-3">{r.title}</h6>
                      <div className="mb-2"><small className="fw-bold text-muted">Filtros:</small></div>
                      {r.filtros.map((f, j) => (
                        <div key={j}><small><i className="bi bi-check2 text-success me-1"></i>{f}</small></div>
                      ))}
                      <div className="mt-2"><small className="fw-bold text-muted">Incluye:</small></div>
                      <small className="text-muted">{r.contenido}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <InfoBox type="info" title="Personalización">
              Los reportes incluyen el logo y nombre de la empresa configurados en el sistema. Pueden exportarse en Excel para procesamiento adicional o en PDF para impresión.
            </InfoBox>
          </div>
        );

      // ─────────────────────────────────
      //  CONCILIACIÓN BANCARIA
      // ─────────────────────────────────
      case 'conciliacion':
        return (
          <div>
            <SectionHeader icon="bi-bank2" title="Conciliación Bancaria" subtitle="Verificación de movimientos bancarios vs. sistema" />

            <InfoBox type="info" title="Propósito">
              La conciliación verifica que las transacciones del sistema (ventas, abonos, gastos) coincidan con el estado de cuenta del banco. Es esencial para la <strong>Ley de Bancarización</strong> en Ecuador: transacciones mayores a $500 deben pasar por el sistema financiero para ser deducibles ante el SRI.
            </InfoBox>

            <Card title="Paso 1 — Configurar cuenta bancaria" icon="bi-1-circle-fill" highlight>
              <StepList steps={[
                { text: 'Vaya a la pestaña <strong>"Cuentas Bancarias"</strong>.' },
                { text: 'Haga clic en <strong>"+ Nueva Cuenta"</strong>.' },
                { text: 'Complete: <strong>Nombre</strong>, <strong>Banco</strong> (Pichincha, Pacífico, Produbanco, Guayaquil, Bolivariano), <strong>Tipo</strong> (Corriente/Ahorros), <strong>Número de cuenta</strong>.' },
                { text: 'Guarde.' },
              ]} />
            </Card>

            <Card title="Paso 2 — Crear período de conciliación" icon="bi-2-circle-fill" highlight>
              <StepList steps={[
                { text: 'Vaya a la pestaña principal <strong>"Conciliación"</strong>.' },
                { text: 'Haga clic en <strong>"+ Nuevo Período"</strong>.' },
                { text: 'Seleccione <strong>Cuenta bancaria</strong>, <strong>Mes</strong> y <strong>Año</strong>.' },
                { text: 'Ingrese <strong>Saldo inicial</strong> y <strong>Saldo final</strong> del banco (según el estado de cuenta).' },
                { text: 'Haga clic en <strong>"Crear Período"</strong>.' },
              ]} />
            </Card>

            <Card title="Paso 3 — Importar estado de cuenta" icon="bi-3-circle-fill" highlight>
              <StepList steps={[
                { text: 'Haga clic en <strong>"Importar Estado de Cuenta"</strong>.' },
                { text: 'Seleccione el archivo <strong>Excel (.xlsx) o CSV</strong> descargado de su banca en línea.' },
                { text: 'El sistema parsea automáticamente las columnas (fecha, descripción, referencia, débitos, créditos, saldo).' },
                { text: 'Confirme la importación.' },
              ]} />
              <InfoBox type="info" title="Formato">
                El sistema detecta automáticamente el formato de bancos ecuatorianos (ej: Banco Pichincha). Para otros formatos, configure el mapeo de columnas en la cuenta bancaria.
              </InfoBox>
            </Card>

            <Card title="Paso 4 — Ejecutar Auto-Match" icon="bi-4-circle-fill" highlight>
              <p>Haga clic en <strong>"Auto-Match"</strong>. El algoritmo ejecuta 4 pasos:</p>
              <div className="table-responsive">
                <table className="table table-bordered table-sm mb-0">
                  <thead className="table-light">
                    <tr><th style={{ width: '40px' }}>#</th><th>Método</th><th style={{ width: '80px' }}>Confianza</th></tr>
                  </thead>
                  <tbody>
                    <tr><td className="fw-bold">1</td><td><strong>Referencia + monto exacto:</strong> Código de autorización, número de documento, referencia de transferencia.</td><td><span className="badge bg-success">95%</span></td></tr>
                    <tr><td className="fw-bold">2</td><td><strong>Monto exacto + fecha cercana (±3 días):</strong> Busca registros con el mismo monto en rango de 3 días.</td><td><span className="badge bg-primary">80%</span></td></tr>
                    <tr><td className="fw-bold">3</td><td><strong>Lotes de tarjeta:</strong> Agrupa ventas Medianet/Payphone por día, resta comisión y compara con depósitos.</td><td><span className="badge bg-info text-dark">85%</span></td></tr>
                    <tr><td className="fw-bold">4</td><td><strong>Reglas automáticas:</strong> Aplica reglas configuradas para cargos bancarios recurrentes.</td><td><span className="badge bg-warning text-dark">90%</span></td></tr>
                  </tbody>
                </table>
              </div>
            </Card>

            <Card title="Paso 5 — Conciliar manualmente" icon="bi-5-circle-fill" highlight>
              <p>Para las líneas que no se emparejaron automáticamente:</p>
              <StepList steps={[
                { text: 'Haga clic en una línea <strong>pendiente</strong> (amarilla) del banco.' },
                { text: 'A la derecha se muestran los registros internos sugeridos.' },
                { text: 'Si encuentra la coincidencia: haga clic en <strong>"Emparejar"</strong>.' },
                { text: 'Si es un cargo bancario (comisión, mantenimiento): haga clic en <strong>"Crear Ajuste"</strong>.' },
                { text: 'Si no aplica: haga clic en <strong>"Ignorar"</strong>.' },
              ]} />
            </Card>

            <Card title="Paso 6 — Configurar reglas (opcional)" icon="bi-6-circle-fill" highlight>
              <p>Para automatizar cargos recurrentes, vaya a la pestaña <strong>"Reglas"</strong>:</p>
              <ul>
                <li><strong>Nombre:</strong> Descripción de la regla.</li>
                <li><strong>Condición:</strong> "Descripción contiene" + texto (ej: "MANTENIMIENTO").</li>
                <li><strong>Tipo:</strong> Débito o Crédito.</li>
                <li><strong>Rango monto:</strong> Mínimo y máximo esperado.</li>
                <li><strong>Acción:</strong> Crear ajuste automáticamente o Ignorar.</li>
              </ul>
            </Card>

            <Card title="Paso 7 — Cerrar período" icon="bi-7-circle-fill" highlight>
              <StepList steps={[
                { text: 'Verifique que Saldo Banco ≈ Saldo Libros.' },
                { text: 'Verifique el porcentaje de conciliación (idealmente 100%).' },
                { text: 'Haga clic en <strong>"Cerrar Período"</strong>.' },
                { text: 'El período queda en el <strong>Historial</strong> para consulta futura.' },
              ]} />
            </Card>

            <InfoBox type="warning" title="Recomendación">
              Realice la conciliación <strong>mensualmente</strong>. Mantenga al día los cargos bancarios y las reglas para agilizar el proceso.
            </InfoBox>
          </div>
        );

      // ─────────────────────────────────
      //  CATÁLOGOS
      // ─────────────────────────────────
      case 'catalogos':
        return (
          <div>
            <SectionHeader icon="bi-tags-fill" title="Catálogos" subtitle="Categorías, Marcas y Proveedores" />
            <p className="lead">Los catálogos son datos maestros que se utilizan en otros módulos. Se recomienda configurarlos antes de empezar a operar.</p>

            <div className="row g-4">
              <div className="col-lg-4">
                <Card title="Categorías" icon="bi-bookmark">
                  <StepList steps={[
                    { text: 'Haga clic en <strong>"+ Nueva Categoría"</strong>.' },
                    { text: 'Ingrese <strong>nombre</strong> y descripción.' },
                    { text: 'Configure el <strong>% IVA</strong> predeterminado.' },
                    { text: 'Guarde.' },
                  ]} />
                  <small className="text-muted">Ejemplos: Armazones, Lentes Oftálmicos, Lentes de Sol, Soluciones, Accesorios.</small>
                </Card>
              </div>
              <div className="col-lg-4">
                <Card title="Marcas" icon="bi-bookmark-star">
                  <StepList steps={[
                    { text: 'Haga clic en <strong>"+ Nueva Marca"</strong>.' },
                    { text: 'Ingrese el <strong>nombre</strong>.' },
                    { text: 'Guarde.' },
                  ]} />
                  <small className="text-muted">Ejemplos: Ray-Ban, Oakley, Essilor, Hoya, CooperVision.</small>
                </Card>
              </div>
              <div className="col-lg-4">
                <Card title="Proveedores" icon="bi-truck">
                  <StepList steps={[
                    { text: 'Haga clic en <strong>"+ Nuevo Proveedor"</strong>.' },
                    { text: 'Complete: código, nombre, RUC, dirección, teléfono, email.' },
                    { text: 'Opcionalmente: contacto y notas.' },
                    { text: 'Guarde.' },
                  ]} />
                  <small className="text-muted">Los proveedores se usan en Compras y Gastos.</small>
                </Card>
              </div>
            </div>
          </div>
        );

      // ─────────────────────────────────
      //  SUCURSALES
      // ─────────────────────────────────
      case 'sucursales':
        return (
          <div>
            <SectionHeader icon="bi-shop-window" title="Sucursales" subtitle="Gestión de locales del negocio" />
            <AdminBadge />

            <Card title="Crear sucursal" icon="bi-plus-circle">
              <StepList steps={[
                { text: 'Haga clic en <strong>"+ Nueva Sucursal"</strong>.' },
                { text: 'Complete: <strong>Código</strong> (ej: "SUC01"), <strong>Nombre</strong>, <strong>Dirección</strong>, <strong>Teléfono</strong>, <strong>Email</strong>, <strong>Ciudad</strong>, <strong>Provincia</strong>.' },
                { text: 'Configure los <strong>códigos SRI</strong>: Establecimiento (ej: "001") y Punto de emisión (ej: "001").' },
                { text: 'Si es la sede principal, marque <strong>"Es Matriz"</strong>.' },
                { text: 'Guarde.' },
              ]} />
            </Card>

            <Card title="Cambiar sucursal activa" icon="bi-arrow-repeat">
              <p className="mb-0">Use el <strong>selector de sucursal</strong> en el encabezado del sistema (esquina superior). Todos los datos y operaciones corresponderán a la sucursal seleccionada.</p>
            </Card>
          </div>
        );

      // ─────────────────────────────────
      //  USUARIOS
      // ─────────────────────────────────
      case 'usuarios':
        return (
          <div>
            <SectionHeader icon="bi-people-fill" title="Usuarios" subtitle="Gestión de acceso al sistema" />
            <AdminBadge />

            <Card title="Crear usuario" icon="bi-person-plus">
              <StepList steps={[
                { text: 'Haga clic en <strong>"+ Nuevo Usuario"</strong>.' },
                { text: 'Complete: <strong>Nombre de usuario</strong>, <strong>Email</strong>, <strong>Contraseña</strong> (mínimo 6 caracteres).' },
                { text: 'Complete: <strong>Nombre completo</strong>, <strong>Cédula</strong>, <strong>Teléfono</strong>.' },
                { text: 'Seleccione el <strong>Rol</strong> (determina los permisos).' },
                { text: 'Seleccione la <strong>Sucursal</strong> asignada.' },
                { text: 'Guarde.' },
              ]} />
            </Card>

            <Card title="Roles del sistema" icon="bi-shield-check">
              <p>El sistema tiene <strong>7 roles predefinidos</strong>. Cada rol otorga acceso a módulos específicos:</p>
              <div className="table-responsive">
                <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.82rem' }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: '110px' }}>Módulo</th>
                      <th className="text-center" title="Administrador"><small>Admin</small></th>
                      <th className="text-center" title="Super Admin"><small>S.Admin</small></th>
                      <th className="text-center" title="Gerente"><small>Gerente</small></th>
                      <th className="text-center" title="Vendedor"><small>Vendedor</small></th>
                      <th className="text-center" title="Optometrista"><small>Optom.</small></th>
                      <th className="text-center" title="Técnico Laboratorio"><small>Téc.Lab</small></th>
                      <th className="text-center" title="Recepcionista"><small>Recep.</small></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Dashboard', true, true, true, true, true, true, true],
                      ['Pacientes / Clientes', true, true, true, true, true, 'L', true],
                      ['Historia Clínica', true, true, true, 'L', true, false, 'L'],
                      ['Órdenes de Trabajo', true, true, true, 'L/C', true, true, 'L'],
                      ['Punto de Venta', true, true, true, true, 'L', false, 'L'],
                      ['Caja Registradora', true, true, true, true, false, false, false],
                      ['Cuentas por Cobrar', true, true, true, true, false, false, false],
                      ['Gastos / Egresos', true, true, true, true, false, false, false],
                      ['Facturación SRI', true, true, true, false, false, false, false],
                      ['Productos', true, true, true, 'L', 'L', 'L', 'L'],
                      ['Compras', true, true, true, false, false, false, false],
                      ['Stock / Kardex', true, true, true, false, false, false, false],
                      ['Transferencias', true, true, true, false, false, false, false],
                      ['Centro de Reportes', true, true, true, false, false, false, false],
                      ['Catálogos', true, true, true, false, false, false, false],
                      ['Conciliación Bancaria', true, true, true, false, false, false, false],
                      ['Configuración', true, true, true, false, false, false, false],
                      ['Usuarios', true, true, 'L', false, false, false, false],
                      ['Sucursales', true, true, false, false, false, false, false],
                      ['API Pública', true, true, false, false, false, false, false],
                      ['Manual de Usuario', true, true, true, true, true, true, true],
                    ].map(([mod, ...perms], i) => (
                      <tr key={i}>
                        <td className="fw-semibold">{mod}</td>
                        {perms.map((p, j) => (
                          <td key={j} className="text-center">
                            {p === true ? <i className="bi bi-check-circle-fill text-success"></i>
                              : p === false ? <i className="bi bi-dash text-muted"></i>
                              : <span className="badge bg-warning text-dark" style={{ fontSize: '0.65rem' }}>{p}</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 d-flex gap-3">
                <small><i className="bi bi-check-circle-fill text-success me-1"></i>Acceso completo</small>
                <small><span className="badge bg-warning text-dark me-1" style={{ fontSize: '0.65rem' }}>L</span>Solo lectura</small>
                <small><span className="badge bg-warning text-dark me-1" style={{ fontSize: '0.65rem' }}>L/C</span>Lectura y creación</small>
                <small><i className="bi bi-dash text-muted me-1"></i>Sin acceso</small>
              </div>
            </Card>

            <Card title="Descripción de roles" icon="bi-person-badge">
              <div className="row g-2">
                {[
                  { rol: 'Admin', color: '#dc3545', desc: 'Acceso total al sistema. Gestiona usuarios, configuración y todos los módulos.' },
                  { rol: 'Super Admin', color: '#6f42c1', desc: 'Acceso total + gestión de API pública y claves de integración.' },
                  { rol: 'Gerente', color: '#0d6efd', desc: 'Gestión de sucursal: ventas, compras, inventario, reportes, facturación, transferencias y configuración.' },
                  { rol: 'Vendedor', color: '#198754', desc: 'Atención al cliente: ventas, caja, cuentas por cobrar, crear órdenes de trabajo.' },
                  { rol: 'Optometrista', color: '#fd7e14', desc: 'Atención clínica: historias clínicas, órdenes de trabajo, consulta de pacientes y ventas.' },
                  { rol: 'Técnico Lab.', color: '#0dcaf0', desc: 'Laboratorio: gestión de órdenes de trabajo, consulta de productos y pacientes.' },
                  { rol: 'Recepcionista', color: '#6c757d', desc: 'Recepción: gestión de pacientes, consulta de historias clínicas, órdenes y ventas.' },
                ].map((r, i) => (
                  <div key={i} className="col-md-6">
                    <div className="d-flex align-items-start p-2 border rounded">
                      <span className="badge me-2 mt-1" style={{ background: r.color, minWidth: '80px', fontSize: '0.7rem' }}>{r.rol}</span>
                      <small className="text-muted">{r.desc}</small>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card title="Desactivar usuario" icon="bi-person-x">
              <p className="mb-0">Edite el usuario y desmarque <strong>"Activo"</strong>. El usuario no podrá iniciar sesión pero sus datos históricos se mantienen.</p>
            </Card>
          </div>
        );

      // ─────────────────────────────────
      //  CONFIGURACIÓN
      // ─────────────────────────────────
      case 'configuracion':
        return (
          <div>
            <SectionHeader icon="bi-gear-fill" title="Configuración" subtitle="Ajustes globales del sistema" />
            <AdminBadge />

            <div className="row g-3">
              {[
                { icon: 'bi-building', title: 'General', desc: 'Nombre empresa, RUC, dirección, logo, IVA predeterminado, moneda.', color: '#0d6efd' },
                { icon: 'bi-file-earmark-check', title: 'SRI (Facturación)', desc: 'Ambiente (pruebas/producción), certificado digital .p12, auto-facturación, régimen.', color: '#198754' },
                { icon: 'bi-envelope', title: 'Correo SMTP', desc: 'Servidor, puerto, credenciales y correo remitente para envío de facturas.', color: '#fd7e14' },
                { icon: 'bi-phone', title: 'Payphone', desc: 'Token, Store ID, ambiente (sandbox/producción), URL de respuesta.', color: '#6f42c1' },
                { icon: 'bi-credit-card-2-front', title: 'Medianet', desc: 'Terminal ID, código de comercio, nombre del comercio.', color: '#0dcaf0' },
                { icon: 'bi-percent', title: 'Comisiones', desc: 'Comisión Medianet (~3.5%), Payphone (~2.5%), plazo máximo de crédito.', color: '#dc3545' },
                { icon: 'bi-robot', title: 'Asistente IA', desc: 'Habilitar/deshabilitar, API key, selección de modelo (GPT-4o, Claude, Gemini, etc.).', color: '#ffc107' },
              ].map((c, i) => (
                <div key={i} className="col-md-6">
                  <div className="d-flex align-items-start p-3 border rounded-3 h-100">
                    <div className="rounded-3 p-2 me-3 d-flex align-items-center justify-content-center" style={{ background: c.color + '15', minWidth: '44px', height: '44px' }}>
                      <i className={`bi ${c.icon}`} style={{ color: c.color, fontSize: '1.2rem' }}></i>
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1">{c.title}</h6>
                      <small className="text-muted">{c.desc}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <InfoBox type="warning" title="Importante">
              Los cambios en la configuración afectan a <strong>todo el sistema</strong>. Cada sección tiene su propio botón "Guardar".
            </InfoBox>
          </div>
        );

      // ─────────────────────────────────
      //  API PÚBLICA
      // ─────────────────────────────────
      case 'api':
        return (
          <div>
            <SectionHeader icon="bi-code-slash" title="API Pública" subtitle="Integración con aplicaciones externas" />
            <AdminBadge />

            <Card title="Crear clave de API" icon="bi-key">
              <StepList steps={[
                { text: 'Vaya a la pestaña <strong>"Claves API"</strong>.' },
                { text: 'Haga clic en <strong>"+ Nueva Clave"</strong>.' },
                { text: 'Ingrese <strong>nombre</strong> descriptivo.' },
                { text: 'Seleccione los <strong>permisos</strong> (ej: catalogo:leer, clientes:leer).' },
                { text: 'Configure el <strong>rate limit</strong> y opcionalmente <strong>IPs permitidas</strong>.' },
                { text: 'Haga clic en <strong>"Crear"</strong>.' },
                { text: '<strong>Copie la clave inmediatamente.</strong> No se podrá recuperar después.' },
              ]} />
            </Card>

            <Card title="Documentación" icon="bi-file-text">
              <p className="mb-0">La pestaña <strong>"Documentación"</strong> contiene la referencia completa: endpoints, parámetros, formatos de respuesta y ejemplos de uso.</p>
            </Card>

            <InfoBox type="danger" title="Seguridad">
              Nunca comparta sus claves de API públicamente. Si una clave se compromete, elimínela inmediatamente y cree una nueva.
            </InfoBox>
          </div>
        );

      // ─────────────────────────────────
      //  FAQ
      // ─────────────────────────────────
      case 'faq':
        return (
          <div>
            <SectionHeader icon="bi-question-circle" title="Preguntas Frecuentes" subtitle="Respuestas a las dudas más comunes" />

            {[
              { q: '¿Cómo puedo ver los datos de otra sucursal?', a: 'Use el selector de sucursal en el encabezado del sistema (esquina superior derecha). Al cambiar de sucursal, todos los datos se actualizan.' },
              { q: '¿Qué hago si olvidé mi contraseña?', a: 'Contacte al administrador del sistema. El administrador puede restablecer su contraseña desde el módulo de Usuarios.' },
              { q: '¿Puedo hacer una venta sin tener la caja abierta?', a: 'No. Debe tener una caja abierta para registrar ventas. Un administrador o gerente debe abrir la caja primero.' },
              { q: '¿Cómo anulo una factura electrónica?', a: 'Debe emitir una Nota de Crédito desde el módulo de Facturación SRI. La NC se envía al SRI y anula la factura original.' },
              { q: '¿El stock se actualiza automáticamente?', a: 'Sí. Se actualiza al: registrar ventas, recibir compras, completar transferencias, registrar ajustes manuales y procesar devoluciones (Notas de Crédito).' },
              { q: '¿Puedo usar el sistema desde el celular?', a: 'Sí. El sistema es responsive y se adapta a pantallas móviles. Sin embargo, para la mejor experiencia se recomienda usar una computadora o tablet.' },
              { q: '¿Con qué frecuencia debo hacer la conciliación bancaria?', a: 'Se recomienda realizarla mensualmente, al recibir el estado de cuenta del banco.' },
              { q: '¿Cómo funcionan las comisiones de tarjeta en la conciliación?', a: 'El sistema agrupa las ventas con tarjeta Medianet/Payphone por día, aplica la comisión configurada (~3.5% y ~2.5% respectivamente) y compara el neto con los depósitos del banco.' },
              { q: '¿Cómo registro un pago parcial de una venta a crédito?', a: 'Vaya a Cuentas por Cobrar, busque la cuenta, haga clic en "Ver Detalle" y registre un abono con el monto y método de pago.' },
              { q: '¿Puedo generar una factura después de hacer la venta?', a: 'Sí. Desde el módulo de Facturación SRI o desde el historial de ventas, puede emitir la factura manualmente.' },
            ].map((faq, i) => (
              <div key={i} className="mb-3">
                <div className="p-3 border rounded-3">
                  <h6 className="fw-bold mb-2"><i className="bi bi-chat-left-dots text-primary me-2"></i>{faq.q}</h6>
                  <p className="mb-0 text-muted" style={{ paddingLeft: '1.75rem' }}>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        );

      // ─────────────────────────────────
      //  CONSEJOS Y ATAJOS
      // ─────────────────────────────────
      case 'atajos':
        return (
          <div>
            <SectionHeader icon="bi-lightning" title="Consejos y Atajos" subtitle="Optimice su uso del sistema" />

            <h5 className="fw-bold mb-3">Flujo de trabajo diario recomendado</h5>
            <div className="p-4 bg-light rounded-3 mb-4">
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {[
                  { icon: 'bi-unlock', label: '1. Abrir Caja' },
                  { icon: 'bi-people', label: '2. Atender Pacientes' },
                  { icon: 'bi-clipboard2-pulse', label: '3. Historia Clínica' },
                  { icon: 'bi-eyeglasses', label: '4. Orden de Trabajo' },
                  { icon: 'bi-cart-check', label: '5. Venta' },
                  { icon: 'bi-file-earmark-check', label: '6. Factura' },
                  { icon: 'bi-lock', label: '7. Cerrar Caja' },
                ].map((s, i) => (
                  <React.Fragment key={i}>
                    <div className="text-center p-2 bg-white rounded-3 border shadow-sm" style={{ minWidth: '110px' }}>
                      <i className={`bi ${s.icon} d-block text-primary`} style={{ fontSize: '1.3rem' }}></i>
                      <small className="fw-semibold">{s.label}</small>
                    </div>
                    {i < 6 && <i className="bi bi-chevron-right text-muted d-none d-md-block"></i>}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <h5 className="fw-bold mb-3">Consejos útiles</h5>
            <div className="row g-3">
              {[
                { icon: 'bi-search', title: 'Búsquedas rápidas', desc: 'En los buscadores de productos y clientes, puede buscar por código, nombre, cédula o cualquier campo visible.' },
                { icon: 'bi-funnel', title: 'Filtros avanzados', desc: 'Todos los listados tienen filtros. Úselos para encontrar información específica rápidamente.' },
                { icon: 'bi-printer', title: 'Reimprimir tickets', desc: 'Desde el historial de ventas puede reimprimir el ticket de cualquier venta anterior.' },
                { icon: 'bi-download', title: 'Exportar datos', desc: 'Inventario y Reportes permiten exportar en Excel y PDF. Use filtros antes de exportar para obtener datos específicos.' },
                { icon: 'bi-arrow-clockwise', title: 'Datos en tiempo real', desc: 'Los datos se actualizan al ingresar a cada módulo. No necesita recargar la página manualmente.' },
                { icon: 'bi-shield-check', title: 'Permisos por rol', desc: 'Cada usuario ve solo los módulos que su rol permite. Si no ve un módulo, contacte al administrador.' },
                { icon: 'bi-globe', title: 'Zona horaria Ecuador', desc: 'El sistema usa UTC-5 (hora Ecuador) para todas las fechas y operaciones.' },
                { icon: 'bi-telephone', title: 'Soporte', desc: 'Para problemas técnicos, contacte al administrador del sistema o al equipo de soporte.' },
              ].map((tip, i) => (
                <div key={i} className="col-md-6">
                  <div className="d-flex align-items-start p-3 border rounded-3 h-100">
                    <i className={`bi ${tip.icon} text-primary me-3 mt-1`} style={{ fontSize: '1.2rem' }}></i>
                    <div>
                      <div className="fw-bold mb-1">{tip.title}</div>
                      <small className="text-muted">{tip.desc}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-5">
            <i className="bi bi-file-earmark-text text-muted" style={{ fontSize: '3rem' }}></i>
            <p className="text-muted mt-3">Seleccione una sección del menú lateral.</p>
          </div>
        );
    }
  };

  // ═══════════════════════════════════════════════════════════
  //  RENDER PRINCIPAL
  // ═══════════════════════════════════════════════════════════
  return (
    <>
      <style>{`
        .manual-sidebar-btn:hover { background: #f0f0f0 !important; }
        .manual-sidebar-btn.active { background: #0d6efd !important; color: white !important; }
        .manual-sidebar-btn.active:hover { background: #0b5ed7 !important; }
        .manual-card { border: 1px solid #e9ecef; border-radius: 12px; overflow: hidden; transition: box-shadow 0.2s; }
        .manual-card:hover { box-shadow: 0 4px 15px rgba(0,0,0,0.08); }
        .manual-card-highlight { border-left: 4px solid #0d6efd; }
        @media (max-width: 991px) {
          .manual-sidebar { position: fixed !important; top: 0; left: 0; bottom: 0; z-index: 1050; }
          .manual-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.4); z-index: 1040; }
        }
      `}</style>

      <div className="d-flex" style={{ minHeight: 'calc(100vh - 130px)', marginLeft: '-1.5rem', marginRight: '-1.5rem', marginTop: '-1rem' }}>

        {/* Overlay mobile */}
        {sidebarOpen && window.innerWidth < 992 && (
          <div className="manual-overlay d-lg-none" onClick={() => setSidebarOpen(false)} />
        )}

        {/* ─── SIDEBAR ─── */}
        {sidebarOpen && (
          <div className="manual-sidebar bg-white border-end shadow-sm d-flex flex-column"
            style={{ width: '270px', minWidth: '270px', maxHeight: 'calc(100vh - 130px)', position: 'sticky', top: '0' }}>

            {/* Header sidebar */}
            <div className="p-3 border-bottom" style={{ background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)' }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <i className="bi bi-book-half text-primary me-2" style={{ fontSize: '1.2rem' }}></i>
                  <span className="fw-bold" style={{ fontSize: '0.95rem' }}>Contenido</span>
                </div>
                <button className="btn btn-sm btn-link text-muted p-0 d-lg-none" onClick={() => setSidebarOpen(false)}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <div className="position-relative">
                <i className="bi bi-search position-absolute text-muted" style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem' }}></i>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ paddingLeft: '30px', borderRadius: '8px', fontSize: '0.82rem' }}
                  placeholder="Buscar sección..."
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                />
              </div>
            </div>

            {/* Menu items */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {grupos.map(grupo => {
                const items = seccionesFiltradas.filter(s => s.grupo === grupo);
                if (items.length === 0) return null;
                return (
                  <div key={grupo}>
                    <div className="px-3 py-2" style={{ background: '#f8f9fa' }}>
                      <small className="fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.8px', color: grupoColors[grupo] || '#6c757d' }}>
                        {grupo}
                      </small>
                    </div>
                    {items.map(s => (
                      <button
                        key={s.id}
                        className={`manual-sidebar-btn btn btn-sm w-100 text-start px-3 py-2 rounded-0 border-0 d-flex align-items-center ${seccionActiva === s.id ? 'active' : ''}`}
                        style={{ fontSize: '0.82rem', transition: 'all 0.15s' }}
                        onClick={() => navTo(s.id)}
                      >
                        <i className={`bi ${s.icon} me-2`} style={{ width: '18px', fontSize: '0.9rem' }}></i>
                        {s.titulo}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Footer sidebar */}
            <div className="p-3 border-top bg-light text-center">
              <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                <i className="bi bi-info-circle me-1"></i>
                Versión del manual: 1.0
              </small>
            </div>
          </div>
        )}

        {/* ─── CONTENIDO PRINCIPAL ─── */}
        <div className="flex-grow-1" ref={contentRef} style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 130px)' }}>
          <div className="p-4" style={{ maxWidth: '920px', margin: '0 auto' }}>

            {/* Mobile toggle + breadcrumb */}
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                {!sidebarOpen && (
                  <button className="btn btn-outline-primary btn-sm me-3" onClick={() => setSidebarOpen(true)}>
                    <i className="bi bi-list"></i>
                  </button>
                )}
                <nav>
                  <ol className="breadcrumb mb-0" style={{ fontSize: '0.82rem' }}>
                    <li className="breadcrumb-item">
                      <button className="btn btn-link p-0 text-decoration-none" style={{ fontSize: '0.82rem' }} onClick={() => navTo('inicio')}>
                        <i className="bi bi-house me-1"></i>Manual
                      </button>
                    </li>
                    {seccionActiva !== 'inicio' && (
                      <li className="breadcrumb-item active">
                        {secciones.find(s => s.id === seccionActiva)?.titulo}
                      </li>
                    )}
                  </ol>
                </nav>
              </div>
              {sidebarOpen && (
                <button className="btn btn-sm btn-outline-secondary d-none d-lg-block" onClick={() => setSidebarOpen(false)} title="Ocultar menú">
                  <i className="bi bi-layout-sidebar-inset"></i>
                </button>
              )}
            </div>

            {/* Contenido dinámico */}
            {renderContenido()}

            {/* Navegación inferior */}
            <div className="d-flex justify-content-between mt-5 pt-4 border-top">
              {(() => {
                const idx = secciones.findIndex(s => s.id === seccionActiva);
                const prev = idx > 0 ? secciones[idx - 1] : null;
                const next = idx < secciones.length - 1 ? secciones[idx + 1] : null;
                return (
                  <>
                    <div>
                      {prev && (
                        <button className="btn btn-outline-secondary btn-sm d-flex align-items-center" onClick={() => navTo(prev.id)}>
                          <i className="bi bi-arrow-left me-2"></i>
                          <div className="text-start">
                            <div style={{ fontSize: '0.65rem', opacity: 0.7 }}>ANTERIOR</div>
                            <div style={{ fontSize: '0.82rem' }}>{prev.titulo}</div>
                          </div>
                        </button>
                      )}
                    </div>
                    <div>
                      {next && (
                        <button className="btn btn-outline-primary btn-sm d-flex align-items-center" onClick={() => navTo(next.id)}>
                          <div className="text-end">
                            <div style={{ fontSize: '0.65rem', opacity: 0.7 }}>SIGUIENTE</div>
                            <div style={{ fontSize: '0.82rem' }}>{next.titulo}</div>
                          </div>
                          <i className="bi bi-arrow-right ms-2"></i>
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


// ═══════════════════════════════════════════════════════════
//  COMPONENTES AUXILIARES REUTILIZABLES
// ═══════════════════════════════════════════════════════════

const SectionHeader = ({ icon, title, subtitle }) => (
  <div className="mb-4 pb-3 border-bottom">
    <div className="d-flex align-items-center mb-1">
      <div className="rounded-3 p-2 me-3 d-flex align-items-center justify-content-center" style={{ background: '#0d6efd15', width: '48px', height: '48px' }}>
        <i className={`bi ${icon}`} style={{ fontSize: '1.5rem', color: '#0d6efd' }}></i>
      </div>
      <div>
        <h3 className="fw-bold mb-0">{title}</h3>
        {subtitle && <small className="text-muted">{subtitle}</small>}
      </div>
    </div>
  </div>
);

const Card = ({ title, icon, children, highlight }) => (
  <div className={`manual-card mb-4 ${highlight ? 'manual-card-highlight' : ''}`}>
    <div className="p-3 border-bottom bg-light d-flex align-items-center">
      <i className={`bi ${icon} text-primary me-2`}></i>
      <h6 className="fw-bold mb-0" style={{ fontSize: '0.95rem' }}>{title}</h6>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const StepList = ({ steps }) => (
  <div className="mb-2">
    {steps.map((step, i) => (
      <div key={i} className="d-flex mb-2">
        <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3 flex-shrink-0"
          style={{ width: '26px', height: '26px', fontSize: '0.75rem', fontWeight: '700', marginTop: '1px' }}>
          {i + 1}
        </div>
        <div style={{ fontSize: '0.9rem', lineHeight: '1.5' }} dangerouslySetInnerHTML={{ __html: step.text }} />
      </div>
    ))}
  </div>
);

const InfoBox = ({ type, title, children, text }) => {
  const config = {
    info: { icon: 'bi-info-circle-fill', bg: '#0d6efd10', border: '#0d6efd40', color: '#0d6efd' },
    warning: { icon: 'bi-exclamation-triangle-fill', bg: '#ffc10710', border: '#ffc10740', color: '#ffc107' },
    success: { icon: 'bi-check-circle-fill', bg: '#19875410', border: '#19875440', color: '#198754' },
    danger: { icon: 'bi-x-octagon-fill', bg: '#dc354510', border: '#dc354540', color: '#dc3545' },
  };
  const c = config[type] || config.info;
  return (
    <div className="rounded-3 p-3 mb-4" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
      <div className="d-flex align-items-start">
        <i className={`bi ${c.icon} me-2 mt-1 flex-shrink-0`} style={{ color: c.color }}></i>
        <div style={{ fontSize: '0.88rem' }}>
          {title && <strong className="d-block mb-1">{title}</strong>}
          {text ? <span dangerouslySetInnerHTML={{ __html: text }} /> : children}
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ color, label, desc }) => (
  <div className="d-flex align-items-center gap-1">
    <span className={`badge bg-${color} ${color === 'warning' || color === 'info' ? 'text-dark' : ''}`}>{label}</span>
    {desc && <small className="text-muted">({desc})</small>}
  </div>
);

const FieldTable = ({ fields }) => (
  <div className="table-responsive mb-4">
    <table className="table table-bordered table-sm">
      <thead className="table-light"><tr><th style={{ width: '180px' }}>Campo</th><th>Descripción</th></tr></thead>
      <tbody>
        {fields.map((f, i) => (
          <tr key={i}><td className="fw-semibold">{f.field}</td><td><small>{f.desc}</small></td></tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TimelineStep = ({ step, isLast, onNavigate }) => (
  <div className="d-flex mb-0">
    <div className="d-flex flex-column align-items-center me-3">
      <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
        style={{ width: '36px', height: '36px', fontSize: '0.85rem', fontWeight: '700', flexShrink: 0 }}>
        {step.num}
      </div>
      {!isLast && <div style={{ width: '2px', background: '#dee2e6', flex: 1, minHeight: '20px' }}></div>}
    </div>
    <div className="pb-4 flex-grow-1">
      <h6 className="fw-bold mb-1">{step.title}</h6>
      <p className="text-muted mb-2" style={{ fontSize: '0.88rem' }}>{step.desc}</p>
      {step.link && (
        <button className="btn btn-sm btn-outline-primary" onClick={() => onNavigate(step.link)}>
          <i className="bi bi-arrow-right me-1"></i>{step.linkText}
        </button>
      )}
    </div>
  </div>
);

const AdminBadge = () => (
  <div className="d-inline-flex align-items-center gap-1 mb-4 px-3 py-2 rounded-3" style={{ background: '#dc354510', border: '1px solid #dc354530' }}>
    <i className="bi bi-shield-lock text-danger"></i>
    <small className="fw-bold text-danger">Solo Administradores</small>
  </div>
);

export default ManualUsuario;
